import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Link, 
  IconButton,
  Divider,
  Stack
} from '@mui/material';
import { 
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  YouTube as YouTubeIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import sharkPanzerLogoWithName from '../assets/sharkpanzer_logo_withname.png';
import unwiringTechLogo from '../assets/unwiringtech_logo.png';

const Footer: React.FC = () => {
  return (
    <Box
      component="footer"
      sx={{
        py: 6,
        px: 2,
        mt: 'auto',
        backgroundColor: '#001a14', // Dark green background similar to Unwiring Studios
        color: 'white'
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h5"
                component={RouterLink}
                to="/"
                sx={{
                  textDecoration: 'none',
                  color: 'inherit',
                  fontWeight: 700,
                  mb: 2,
                  display: 'block'
                }}
              >
                <span className="gradient-text">SharkPanzer</span>
              </Typography>
              <Typography variant="body2" color="grey.400" paragraph>
                Empowering learners with AI-powered video knowledge capsules. Transform videos from YouTube, Instagram, offline sources, or any social media platform into structured, digestible content and save time while learning more effectively.
              </Typography>
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                <img 
                  src={sharkPanzerLogoWithName} 
                  alt="SharkPanzer Logo" 
                  style={{ height: 60, width: 'auto' }} 
                />
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              COMPANY
            </Typography>
            <Stack spacing={1}>
              <Link component={RouterLink} to="/about" color="grey.400" sx={{ textDecoration: 'none', '&:hover': { color: 'white' } }}>
                About Us
              </Link>
              <Link component={RouterLink} to="/work" color="grey.400" sx={{ textDecoration: 'none', '&:hover': { color: 'white' } }}>
                Our Work
              </Link>
              <Link component={RouterLink} to="/pricing" color="grey.400" sx={{ textDecoration: 'none', '&:hover': { color: 'white' } }}>
                Pricing
              </Link>
              <Link component={RouterLink} to="/testimonials" color="grey.400" sx={{ textDecoration: 'none', '&:hover': { color: 'white' } }}>
                Testimonials
              </Link>
              <Link component={RouterLink} to="/contact" color="grey.400" sx={{ textDecoration: 'none', '&:hover': { color: 'white' } }}>
                Contact Us
              </Link>
            </Stack>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              SERVICES
            </Typography>
            <Stack spacing={1}>
              <Link component={RouterLink} to="/privacy" color="grey.400" sx={{ textDecoration: 'none', '&:hover': { color: 'white' } }}>
                Privacy Policy
              </Link>
              <Link component={RouterLink} to="/terms" color="grey.400" sx={{ textDecoration: 'none', '&:hover': { color: 'white' } }}>
                Terms and Conditions
              </Link>
              <Link component={RouterLink} to="/refund" color="grey.400" sx={{ textDecoration: 'none', '&:hover': { color: 'white' } }}>
                Refund Policy
              </Link>
              <Link component={RouterLink} to="/shipping" color="grey.400" sx={{ textDecoration: 'none', '&:hover': { color: 'white' } }}>
                Shipping Policy
              </Link>
              <Link component={RouterLink} to="/pricing" color="grey.400" sx={{ textDecoration: 'none', '&:hover': { color: 'white' } }}>
                Pricing Policy
              </Link>
              <Link component={RouterLink} to="/support" color="grey.400" sx={{ textDecoration: 'none', '&:hover': { color: 'white' } }}>
                Support Center
              </Link>
            </Stack>
            
            <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
              FOLLOW US
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton aria-label="facebook" size="small" sx={{ color: 'grey.400', '&:hover': { color: 'white' } }}>
                <FacebookIcon />
              </IconButton>
              <IconButton aria-label="instagram" size="small" sx={{ color: 'grey.400', '&:hover': { color: 'white' } }}>
                <InstagramIcon />
              </IconButton>
              <IconButton aria-label="linkedin" size="small" sx={{ color: 'grey.400', '&:hover': { color: 'white' } }}>
                <LinkedInIcon />
              </IconButton>
              <IconButton aria-label="youtube" size="small" sx={{ color: 'grey.400', '&:hover': { color: 'white' } }}>
                <YouTubeIcon />
              </IconButton>
              <IconButton aria-label="twitter" size="small" sx={{ color: 'grey.400', '&:hover': { color: 'white' } }}>
                <TwitterIcon />
              </IconButton>
            </Box>
            
            <Box sx={{ 
              mt: 3, 
              py: 2,
              pl: 2,
              pr: 2,
              border: '1px solid rgba(255,255,255,0.1)', 
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}>
              <EmailIcon sx={{ color: 'grey.400' }} />
              <Typography variant="body2" color="grey.400">
                support@unwiringtech.com
              </Typography>
            </Box>
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.1)' }} />
        
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            <Typography variant="body2" color="grey.400">
              SharkPanzer is a product of UnwiringTech®
            </Typography>
          </Box>
          
          <Box sx={{ mb: 2 }}>
            <img 
              src={unwiringTechLogo} 
              alt="UnwiringTech Logo" 
              style={{ height: 30, width: 'auto' }} 
            />
          </Box>
          
          <Typography variant="body2" color="grey.400" sx={{ mb: 1 }}>
            © {new Date().getFullYear()} Unwiring Tech®
          </Typography>
          <Typography variant="body2" color="grey.400" sx={{ mt: 1 }}>
            All Rights Reserved
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
